/**
 * Portfolio route qualifier (chat-centric revamp Task 9 navigation sweep).
 *
 * The portfolio components (PositionTable, TradesScreen, WatchlistScreen,
 * PortfolioThesisForm) navigate by bare PortfolioNavigator screen names,
 * which only resolve while they sit INSIDE the Portfolio navigator — the
 * legacy Portfolio tab. Mounted from the Book tab (they are no longer tabs),
 * those names are dead, so the Book screen provides a qualifier through this
 * context and the same components navigate via the root stack instead:
 *
 *   navigate('Portfolio', { screen: 'PositionDetail', params: {...} })
 *
 * With no provider (the legacy Portfolio tab) usePortfolioNavigate falls
 * back to the exact pre-sweep bare navigation, so PortfolioTab behavior and
 * its tests are unchanged.
 */

import { createContext, useContext, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { PortfolioNavigatorParamList } from './PortfolioNavigator';

export type PortfolioScreenKey = keyof PortfolioNavigatorParamList;

export type PortfolioNavigateFn = (
  screen: PortfolioScreenKey,
  params?: Record<string, unknown>
) => void;

/** null = no qualifier in scope → bare (legacy) navigation. */
export const PortfolioQualifierContext = createContext<PortfolioNavigateFn | null>(null);

export function usePortfolioNavigate(): PortfolioNavigateFn {
  const qualifier = useContext(PortfolioQualifierContext);
  const navigation = useNavigation<{
    navigate: (screen: string, params?: Record<string, unknown>) => void;
  }>();

  return useMemo(
    () =>
      qualifier
        ? qualifier
        : (screen, params) =>
            params === undefined
              ? navigation.navigate(screen)
              : navigation.navigate(screen, params),
    [qualifier, navigation]
  );
}
